import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Alert
} from '@mui/material';
import { 
  Close as CloseIcon, 
  Warning as WarningIcon,
  Delete as DeleteIcon 
} from '@mui/icons-material';
const DeleteRatesDialog = ({ open, onClose, employee, onConfirm, loading = false }) => {
  const [deleting, setDeleting] = React.useState(false);
  const isBusy = loading || deleting;

  const handleConfirm = async () => {
    if (!onConfirm || !employee) return;
    try {
      setDeleting(true);
      await onConfirm(employee);
      onClose();
    } finally {
      setDeleting(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  if (!employee) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxWidth: '500px'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1,
        borderBottom: '1px solid #e5e7eb'
      }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 600, color: '#374151' }}>
          Delete Rates
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: '#6b7280',
            '&:hover': { bgcolor: '#f3f4f6' }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <Alert 
            severity="warning" 
            icon={<WarningIcon />}
            sx={{ 
              '& .MuiAlert-message': { 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1 
              }
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Are you sure you want to delete all rates for this employee?
            </Typography>
          </Alert>

          <Box sx={{ 
            p: 2, 
            bgcolor: '#f8f9fa', 
            borderRadius: 1, 
            border: '1px solid #e5e7eb' 
          }}>
            <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
              Employee Details:
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#374151' }}>
              {employee.displayName || employee.name || '—'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              ID: {employee._id || employee.id || '—'}
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ color: '#6b7280', lineHeight: 1.6 }}>
            This action will permanently delete all rate configurations for this employee, including:
          </Typography>
          
          <Box component="ul" sx={{ 
            pl: 2, 
            m: 0, 
            color: '#6b7280',
            '& li': { 
              fontSize: '0.875rem', 
              mb: 0.5 
            }
          }}>
            <li>Daily rates and overtime calculations</li>
            <li>Holiday and special holiday rates</li>
            <li>Government contributions (SSS, Pag-ibig, Philhealth)</li>
            <li>Allowances and other benefits</li>
          </Box>

          <Typography variant="body2" sx={{ 
            color: '#dc2626', 
            fontWeight: 500,
            bgcolor: '#fef2f2',
            p: 1.5,
            borderRadius: 1,
            border: '1px solid #fecaca'
          }}>
            ⚠️ This action cannot be undone. Please make sure you want to proceed.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 2, borderTop: '1px solid #e5e7eb' }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            borderColor: '#e5e7eb',
            color: '#6b7280',
            textTransform: 'none',
            px: 3,
            py: 1,
            '&:hover': {
              borderColor: '#12C1DC',
              color: '#12C1DC'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          startIcon={<DeleteIcon />}
          disabled={isBusy}
          sx={{
            bgcolor: '#EF4444',
            '&:hover': { bgcolor: '#DC2626' },
            textTransform: 'none',
            px: 3,
            py: 1
          }}
        >
          {isBusy ? 'Deleting...' : 'Delete Rates'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteRatesDialog;
